import { useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { StyledTabs } from "core/components/StyledTabs";
import { GridAreaContainer } from "core/components/GridAreaContainer";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { WidgetTabValues } from "features/DMS/entities";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { DMSWidgetList } from "./DMSWidgetList";
import { useDMSWidgetData } from "./useDMSWidgetData";

export const sortingTabMap: Record<
  WidgetTabValues,
  "publishTime desc" | "lastModificationTime desc"
> = {
  publishTime: "publishTime desc",
  lastModificationTime: "lastModificationTime desc",
};

export function DMSWidget() {
  const RESULTS_PER_PAGE = 5;
  const [selectedTab, setSelectedTab] =
    useState<WidgetTabValues>("publishTime");
  const [pageIndex, setPageIndex] = useState(0);
  // const [tabIndex, setTabIndex] = useState(0);
  const sorting = sortingTabMap[selectedTab];
  const isDocumentModified =
    selectedTab === "lastModificationTime" ? true : undefined;
  const skipCount = pageIndex * RESULTS_PER_PAGE;
  const { isLoading, items, totalCount } = useDMSWidgetData({
    skipCount,
    maxResultCount: RESULTS_PER_PAGE,
    sorting,
    isDocumentModified,
  });
  // pre-load next page
  useDMSWidgetData({
    skipCount: skipCount + RESULTS_PER_PAGE,
    maxResultCount: RESULTS_PER_PAGE,
    sorting,
    isDocumentModified,
  });
  const rangeStart = RESULTS_PER_PAGE * pageIndex + 1;
  let rangeEnd = RESULTS_PER_PAGE * pageIndex + RESULTS_PER_PAGE;
  rangeEnd = rangeEnd > totalCount ? totalCount : rangeEnd;
  const backArrowDisabled = pageIndex === 0;
  const frontArrowDisabled = (pageIndex + 1) * RESULTS_PER_PAGE >= totalCount;
  const { t } = useTranslation("DocumentManagementSystem", {
    keyPrefix: "Next:DocumentManagementSystem:DMSWidget",
  });
  function next() {
    setPageIndex((i) => i + 1);
  }
  function previous() {
    setPageIndex((i) => i - 1);
  }
  function changeTab(newTab: WidgetTabValues) {
    setSelectedTab((tab) => {
      if (tab === newTab) {
        return tab;
      }
      setPageIndex(0);
      return newTab;
    });
  }
  const { isMobile } = useBreakpoints();
  return (
    <GridAreaContainer gridArea="dms" title={t("gridAreaContainerTitle")}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: isMobile ? "700px" : "570px",
          py: 4,
          px: isMobile ? 1 : 4,
        }}
      >
        <StyledTabs
          value={selectedTab}
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab
            value="publishTime"
            label={t("tabLastPublishTime")}
            onClick={() => changeTab("publishTime")}
          />
          <Tab
            value="lastModificationTime"
            label={t("tabLastModificationTime")}
            onClick={() => changeTab("lastModificationTime")}
          />
        </StyledTabs>
        <Divider />
        <Box minHeight="375px" overflow="auto">
          <DMSWidgetList
            sx={{
              mb: 4,
            }}
            isLoading={isLoading}
            documents={items}
            selectedTab={selectedTab}
          />
        </Box>
        <Stack
          flex={1}
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          gap={2}
        >
          {isLoading ? (
            <CircularProgress size="2rem" />
          ) : (
            <Typography>
              {t("paginationStatus", {
                rangeStart,
                rangeEnd,
                totalCount,
              })}
            </Typography>
          )}

          <ButtonGroup>
            <Button disabled={backArrowDisabled} onClick={previous}>
              <ChevronLeft />
            </Button>
            <Button disabled={frontArrowDisabled} onClick={next}>
              <ChevronRight />
            </Button>
          </ButtonGroup>
        </Stack>
      </Paper>
    </GridAreaContainer>
  );
}
